/**
 * Account Settings Store
 * 계정 설정 관련 상태 관리
 */

import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { accountSettingsApi, type AccountInfo } from '../api/accountSettingsApi';
import { useSessionStore } from '@/shared/stores/sessionStore';

export interface UpdateAccountData {
  fullName?: string;
  firstName?: string;
  lastName?: string;
  middleName?: string;
  defaultLangCd?: string;
  dateFormat?: string;
  userRole?: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const useAccountSettingsStore = defineStore('accountSettings', () => {
  // State
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const uploadProgress = ref(0);
  const accountInfo = ref<AccountInfo | null>(null);

  // Getters
  const getIsLoading = computed(() => isLoading.value);
  const getError = computed(() => error.value);
  const getUploadProgress = computed(() => uploadProgress.value);
  const getAccountInfo = computed(() => accountInfo.value);

  // Actions
  /**
   * 계정 정보 조회 (DB에서 최신 데이터)
   */
  const fetchAccount = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await accountSettingsApi.getAccount() as any;

      if (response.user) {
        accountInfo.value = response.user;
        return { success: true, user: response.user };
      } else {
        throw new Error('Failed to fetch account info');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to fetch account info';
      console.error('Account fetch error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 프로필 이미지 업로드
   */
  const uploadProfileImage = async (file: File) => {
    isLoading.value = true;
    error.value = null;
    uploadProgress.value = 0;

    try {
      // apiClient.post() returns ApiResponse<T>, but backend sends { success: true, url, imageUrl, message }
      const response = await accountSettingsApi.uploadProfileImage(file) as any;

      console.log('[AccountSettingsStore] Full upload response:', {
        response,
        hasUrl: !!response.url,
        hasImageUrl: !!response.imageUrl,
        url: response.url,
        imageUrl: response.imageUrl
      });

      if (response.url || response.imageUrl) {
        uploadProgress.value = 100;
        return {
          success: true,
          url: response.url,
          imageUrl: response.imageUrl
        };
      } else {
        throw new Error('Upload failed: No URL returned');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to upload profile image';
      console.error('Profile image upload error:', err);
      throw err;
    } finally {
      isLoading.value = false;
      uploadProgress.value = 0;
    }
  };

  /**
   * 프로필 이미지 삭제
   */
  const deleteProfileImage = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await accountSettingsApi.deleteProfileImage() as any;
      
      console.log('[AccountSettingsStore] Full delete response:', {
        response,
        hasData: !!response.data,
        hasSuccess: !!response.success,
        dataSuccess: response.data?.success,
        responseSuccess: response.success,
        responseKeys: Object.keys(response || {}),
        dataKeys: response.data ? Object.keys(response.data) : []
      });
      
      // apiClient.delete()는 response.data를 반환하므로
      // 백엔드에서 sendSuccessResponse로 { success: true, ...data }를 반환하면
      // response는 { success: true, ...data } 형식
      if (response.success === true) {
        // 세션 스토어 업데이트 (필요시)
        const sessionStore = useSessionStore();
        const userInfo = sessionStore.getUserInfo();
        if (userInfo) {
          // sessionStore.updateUserProfile({ profileImage: null });
        }
        
        return { success: true };
      } else {
        // 응답에 success가 없거나 false인 경우
        console.warn('[AccountSettingsStore] Delete response missing success flag:', response);
        throw new Error('Delete failed: No success flag in response');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to delete profile image';
      console.error('Profile image delete error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 계정 정보 업데이트
   */
  const updateAccount = async (data: UpdateAccountData) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await accountSettingsApi.updateAccount(data) as any;
      
      console.log('[AccountSettingsStore] Full update response:', {
        response,
        hasData: !!response.data,
        hasSuccess: !!response.success,
        dataSuccess: response.data?.success,
        responseSuccess: response.success,
        responseKeys: Object.keys(response || {}),
        dataKeys: response.data ? Object.keys(response.data) : []
      });
      
      // apiClient.patch()는 response.data를 반환하므로
      // 백엔드에서 sendSuccessResponse로 { success: true, ...data }를 반환하면
      // response는 { success: true, ...data } 형식
      if (response.success === true || response.data?.success === true) {
        // 세션 스토어 업데이트 (필요시)
        const sessionStore = useSessionStore();
        // 세션 정보 새로고침
        await sessionStore.refreshSession();
        
        return { success: true };
      } else {
        // 응답에 success가 없거나 false인 경우
        console.warn('[AccountSettingsStore] Update response missing success flag:', response);
        throw new Error('Update failed: No success flag in response');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to update account';
      console.error('Account update error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 비밀번호 변경
   */
  const changePassword = async (data: ChangePasswordData) => {
    isLoading.value = true;
    error.value = null;

    console.log('[AccountSettingsStore] 비밀번호 변경 요청:', {
      hasCurrentPassword: !!data.currentPassword,
      currentPasswordLength: data.currentPassword?.length || 0,
      hasNewPassword: !!data.newPassword,
      newPasswordLength: data.newPassword?.length || 0,
      hasConfirmPassword: !!data.confirmPassword,
      confirmPasswordLength: data.confirmPassword?.length || 0
    });

    try {
      const response = await accountSettingsApi.changePassword(data);
      
      console.log('[AccountSettingsStore] 비밀번호 변경 응답:', response);
      
      if (response.data?.success || response.success) {
        return { success: true };
      } else {
        throw new Error('Password change failed');
      }
    } catch (err: any) {
      console.error('[AccountSettingsStore] 비밀번호 변경 에러:', {
        error: err,
        response: err.response,
        data: err.response?.data,
        message: err.message
      });
      
      // 백엔드에서 반환한 에러 메시지 추출
      error.value = err.response?.data?.message || 
                   err.response?.data?.error?.message ||
                   err.message || 
                   'Failed to change password';
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * MFA 상태 조회
   */
  const getMFAStatus = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await accountSettingsApi.getMFAStatus() as any;
      
      if (response.isEnabled !== undefined) {
        return {
          isEnabled: response.isEnabled,
          hasSecretKey: response.hasSecretKey || false,
          setupDate: response.setupDate || null
        };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to get MFA status';
      console.error('MFA status error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * MFA QR 코드 생성
   */
  const generateMFAQR = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await accountSettingsApi.generateMFAQR() as any;
      
      if (response.qrCodeUrl && response.secretKey) {
        return {
          qrCodeUrl: response.qrCodeUrl,
          secretKey: response.secretKey,
          manualEntryKey: response.manualEntryKey || response.secretKey
        };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to generate MFA QR code';
      console.error('MFA QR generation error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * MFA 검증 및 활성화
   */
  const verifyAndEnableMFA = async (secretKey: string, token: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await accountSettingsApi.verifyAndEnableMFA({ secretKey, token }) as any;
      
      if (response.verified && response.mfaEnabled) {
        return {
          verified: true,
          mfaEnabled: true,
          message: response.message || 'MFA has been enabled successfully.'
        };
      } else {
        throw new Error(response.message || 'MFA verification failed');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to verify and enable MFA';
      console.error('MFA verification error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * MFA 비활성화
   */
  const disableMFA = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await accountSettingsApi.disableMFA() as any;
      
      if (response.mfaEnabled === false) {
        return {
          mfaEnabled: false,
          message: response.message || 'MFA has been disabled successfully.'
        };
      } else {
        throw new Error('Failed to disable MFA');
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || err.message || 'Failed to disable MFA';
      console.error('MFA disable error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * 에러 클리어
   */
  const clearError = () => {
    error.value = null;
  };

  return {
    // State
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    uploadProgress: computed(() => uploadProgress.value),
    accountInfo: computed(() => accountInfo.value),

    // Actions
    fetchAccount,
    uploadProfileImage,
    deleteProfileImage,
    updateAccount,
    changePassword,
    getMFAStatus,
    generateMFAQR,
    verifyAndEnableMFA,
    disableMFA,
    clearError
  };
});

